import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpService } from 'src/app/service/http/http.service';
import { sortByRegExp } from 'src/app/shared/functions/functionGeneric';
import moment from 'moment';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

interface DialogData {
  selectedTestCases?: string[];
  isEdit?: boolean;
  dataSchedule?: any;
  title?: string
}

@Component({
  selector: 'app-schedule-modal',
  templateUrl: './schedule-modal.component.html',
  styleUrls: ['./schedule-modal.component.scss'],
})
export class ScheduleModalComponent implements OnInit, OnDestroy {
  form: FormGroup;
  executionType: 'once' | 'recurring' = 'once';
  submitLabel = 'CRIAR AGENDAMENTO';
  selectedTestCases: string[] = [];
  private initialScheduleBranch: string | null = null;
  private readonly testCasesPageSize = 50;
    
  branches: string[] = []
  filteredBranches: string[] = []
  
  weekDays = [
    { label: 'Dom', value: 'Dom' },
    { label: 'Seg', value: 'Seg' },
    { label: 'Ter', value: 'Ter' },
    { label: 'Qua', value: 'Qua' },
    { label: 'Qui', value: 'Qui' },
    { label: 'Sex', value: 'Sex' },
    { label: 'Sáb', value: 'Sáb' },
  ];

  simpleFrequencyOptions = [
    { value: 'daily', label: 'Todo dia' },
    { value: 'weekdays', label: 'Dias úteis (Seg-Sex)' },
    { value: 'weekly', label: 'Semanal (dias específicos)' },
    { value: 'monthly', label: 'Mensal (dia específico)' },
  ];

  frequencyModeOptions = [
    { value: 'simple', label: 'Frequência simples (dia/semana/mês)' },
    { value: 'interval', label: 'Intervalo (a cada X minutos/horas)' },
  ];

  intervalUnitOptions = [
    { value: 'minutes', label: 'Minutos' },
    { value: 'hours', label: 'Horas' },
  ];

  getDaysInCurrentMonth() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  }

  monthDays = Array.from({ length: this.getDaysInCurrentMonth() }, (_, i) => ({
    value: (i + 1).toString(),
    label: `Dia ${i + 1}`,
  }));
  
  testCasesList: Array<{ name: string }> = [];
  filteredTestCasesList: Array<{ name: string }> = [];
  readonly selectAllTestCasesValue = '__select_all_test_cases__';

  branchSearchControl = new FormControl('');
  testCaseSearchControl = new FormControl('');

  @ViewChild('branchSearchInput') branchSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('testCasesSearchInput') testCasesSearchInput?: ElementRef<HTMLInputElement>;

  private readonly destroy$ = new Subject<void>();

  trackByTestCase(_: number, testCase: { name: string }): string {
    return testCase?.name;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<ScheduleModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private services: HttpService
  ) {
    
    this.form = this.formBuilder.group({
      name: ['', [Validators.required]],
      branch: ['', [Validators.required]],
      description: [''],
      executionType: ['once', Validators.required],
      date: [''],
      time: ['09:00'],
      frequencyMode: ['simple'],
      simpleFrequency: ['daily'],
      weekDays: [[]],
      monthDay: ['1'],
      intervalUnit: ['minutes'],
      intervalValue: [10],
    });

    this.selectedTestCases = (this.data?.selectedTestCases && [...this.data.selectedTestCases])
      || (this.data?.dataSchedule?.testCases && [...this.data.dataSchedule.testCases])
      || [];

    this.form.addControl('testCases', this.formBuilder.control(this.selectedTestCases));
    this.form.get('testCases')?.valueChanges.subscribe((value: string[]) => {
      this.selectedTestCases = value || [];
    });

    this.executionType = this.form.get('executionType').value;

    this.filterBranches(this.branchSearchControl.value);
    this.filterTestCases(this.testCaseSearchControl.value);

    this.branchSearchControl.valueChanges
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((value) => this.filterBranches(value));

    this.testCaseSearchControl.valueChanges
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((value) => this.filterTestCases(value));

    // Validação condicional baseada no tipo de execução
    this.form.get('executionType').valueChanges.subscribe((value) => {
      this.executionType = value;
      if (value === 'once') {
        this.form.get('date').setValidators([Validators.required]);
        this.form.get('time').setValidators([Validators.required]);
        this.form.get('simpleFrequency').clearValidators();
        this.form.get('weekDays').clearValidators();
        this.form.get('monthDay').clearValidators();
        this.form.get('intervalValue').clearValidators();
      } else {
        this.form.get('date').clearValidators();
        this.form.get('time').clearValidators();
        this.updateRecurringValidators();
      }
      this.form.get('date').updateValueAndValidity();
      this.form.get('time').updateValueAndValidity();
      this.form.get('simpleFrequency').updateValueAndValidity();
      this.form.get('intervalValue').updateValueAndValidity();
    });

    // Validação quando muda frequência simples
    this.form.get('simpleFrequency').valueChanges.subscribe(() => {
      this.updateRecurringValidators();
    });

    // Validação quando muda o modo de frequência (simples x intervalo)
    this.form.get('frequencyMode').valueChanges.subscribe(() => {
      this.updateRecurringValidators();
    });

    // Recalcula limites quando a unidade do intervalo muda (ex: minutos -> horas)
    this.form.get('intervalUnit').valueChanges.subscribe(() => {
      this.clampIntervalValue();
      this.updateRecurringValidators();
    });
  }

  get selectedTestCasesCount(): number {
    return this.form.get('testCases')?.value?.length || 0;
  }

  get selectedTestCasesValues(): string[] {
    return this.form.get('testCases')?.value || [];
  }

  get frequencyMode(): 'simple' | 'interval' {
    return this.form.get('frequencyMode')?.value || 'simple';
  }

  get isIntervalMode(): boolean {
    return this.executionType === 'recurring' && this.frequencyMode === 'interval';
  }

  get intervalMax(): number {
    return this.form.get('intervalUnit').value === 'hours' ? 23 : 59;
  }

  private clampIntervalValue(): void {
    const control = this.form.get('intervalValue');
    const max = this.intervalMax;
    const value = Number(control.value);
    if (!value || value < 1) {
      control.setValue(1, { emitEvent: false });
    } else if (value > max) {
      control.setValue(max, { emitEvent: false });
    }
  }

  private updateRecurringValidators() {
    if (this.executionType !== 'recurring') {
      return;
    }

    if (this.frequencyMode === 'interval') {
      this.form.get('simpleFrequency').clearValidators();
      this.form.get('weekDays').clearValidators();
      this.form.get('monthDay').clearValidators();
      this.form.get('intervalValue').setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(this.intervalMax),
      ]);
      this.form.get('weekDays').updateValueAndValidity();
      this.form.get('monthDay').updateValueAndValidity();
      this.form.get('intervalValue').updateValueAndValidity();
      return;
    }

    const freq = this.form.get('simpleFrequency').value;
    this.form.get('simpleFrequency').setValidators([Validators.required]);
    this.form.get('intervalValue').clearValidators();

    if (freq === 'weekly') {
      this.form.get('weekDays').setValidators([(control) => {
        return control.value && control.value.length > 0 ? null : { required: true };
      }]);
    } else {
      this.form.get('weekDays').clearValidators();
    }

    if (freq === 'monthly') {
      this.form.get('monthDay').setValidators([Validators.required]);
    } else {
      this.form.get('monthDay').clearValidators();
    }

    this.form.get('weekDays').updateValueAndValidity();
    this.form.get('monthDay').updateValueAndValidity();
    this.form.get('intervalValue').updateValueAndValidity();
  }

  ngOnInit(): void {
    this.getBranches();
    if (this.data?.isEdit && this.data.dataSchedule) {
      this.getCts()
      this.submitLabel = 'SALVAR ALTERAÇÕES';
      this.populateFormWithSchedule(this.data.dataSchedule);
    }
  }

  getFrequencyPreview(): string {
    if (this.executionType === 'once') {
      const date = this.form.get('date').value;
      const time = this.form.get('time').value;
      if (date && time) {
        return `Execução única em ${moment(date).format('DD/MM/YYYY')} às ${time}`;
      }
      return 'Selecione a data e horário';
    }

    if (this.frequencyMode === 'interval') {
      const unit = this.form.get('intervalUnit').value;
      const value = this.form.get('intervalValue').value;
      if (!value) {
        return 'Informe o intervalo de execução';
      }
      const unitLabel = unit === 'hours'
        ? (value === 1 ? 'hora' : 'horas')
        : (value === 1 ? 'minuto' : 'minutos');
      return `A cada ${value} ${unitLabel}`;
    }

    const freq = this.form.get('simpleFrequency').value;
    const time = this.form.get('time').value;

    switch (freq) {
      case 'daily':
        return `Todo dia às ${time}`;
      case 'weekdays':
        return `Dias úteis (Seg-Sex) às ${time}`;
      case 'weekly':
        const weekDays = this.form.get('weekDays').value;
        if (!weekDays || weekDays.length === 0) {
          return 'Selecione pelo menos um dia da semana';
        }
        const days = weekDays.sort().join(', ');
        return `Toda semana: ${days} às ${time}`;
      case 'monthly':
        const monthDay = this.form.get('monthDay').value;
        return `Todo dia ${monthDay} do mês às ${time}`;
      default:
        return '';
    }
  }

  toggleWeekDay(day: string) {
    const weekDays = this.form.get('weekDays').value || [];
    const index = weekDays.indexOf(day);
    if (index > -1) {
      weekDays.splice(index, 1);
    } else {
      weekDays.push(day);
    }
    this.form.get('weekDays').setValue([...weekDays]);
  }

  isWeekDaySelected(day: string): boolean {
    const weekDays = this.form.get('weekDays').value || [];
    return weekDays.includes(day);
  }

  save() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.value;
    const body: any = {
      name: formValue.name,
      branch: formValue.branch,
      description: formValue.description,
      executionType: formValue.executionType,
      testCases: formValue.testCases || [],
      frequencyMode: formValue.executionType === 'recurring' ? formValue.frequencyMode : 'simple',
    };

    if (formValue.executionType === 'once') {
      if (formValue.time) {
        const [hours, minutes] = formValue.time.split(':').map(Number);
        body.time = { hour: hours, minute: minutes };
      }
      body.date = formValue.date;
    } else if (formValue.frequencyMode === 'interval') {
      body.intervalUnit = formValue.intervalUnit;
      body.intervalValue = Number(formValue.intervalValue);
      body.cronExpression = this.buildIntervalCronExpression(formValue.intervalUnit, Number(formValue.intervalValue));
    } else {
      if (formValue.time) {
        const [hours, minutes] = formValue.time.split(':').map(Number);
        body.time = { hour: hours, minute: minutes };
      }
      body.simpleFrequency = formValue.simpleFrequency;
      if (formValue.simpleFrequency === 'weekly') {
        body.weekDays = this.traduzirDias(formValue.weekDays || []);
      }
      if (formValue.simpleFrequency === 'monthly') {
        body.monthDay = formValue.monthDay;
      }
    }

    this.dialogRef.close(body);
  }

  /**
   * Gera expressão cron padrão (5 campos: min hora dia mês dia-da-semana)
   * para execução recorrente por intervalo, ex: "*\/10 * * * *" (a cada 10 min)
   * ou "0 *\/2 * * *" (a cada 2 horas).
   */
  private buildIntervalCronExpression(unit: 'minutes' | 'hours', value: number): string {
    if (unit === 'hours') {
      return `0 */${value} * * *`;
    }
    return `*/${value} * * * *`;
  }

  getCts(){
    const projectId = localStorage.getItem('id')
    if (!projectId) {
      this.testCasesList = [];
      this.filteredTestCasesList = [];
      return;
    }
    this.testCasesList = [];
    this.filteredTestCasesList = [];
    this.fetchTestCasesRecursively(projectId, 1, []);
  }

  getBranches() {
    const project = localStorage.getItem('project');
    if (!project) {
      console.error('ProjectId is missing');
      return;
    }
    
    this.services.genericGetNoProject(`projects/${project}/branches`, true).subscribe((res: any) => {
      this.branches = res
        .map((el) => el.replace('refs/heads/', ''))
        .sort(sortByRegExp);

      if (this.initialScheduleBranch) {
        if (!this.branches.includes(this.initialScheduleBranch)) {
          this.branches = [...this.branches, this.initialScheduleBranch];
        }
        this.form.get('branch').setValue(this.initialScheduleBranch, { emitEvent: false });
      }
      this.filterBranches(this.branchSearchControl.value);
    });
  }

  traduzirDias(diasString: string[]) {
    if (!diasString || diasString.length === 0) return [];
    
    const diasSemanaIngles = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const diasSemanaPortugues = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    
    return diasString.map((dia) => {
      const index = diasSemanaPortugues.indexOf(dia);
      return index !== -1 ? diasSemanaIngles[index] : dia;
    });
  }

  private populateFormWithSchedule(schedule: any) {
    const timeString = this.buildTimeString(schedule?.time);
    const weekDaysBR = this.translateWeekDaysToPortuguese(schedule?.weekDays);
    this.initialScheduleBranch = schedule?.branch || null;

    this.form.patchValue({
      name: schedule?.name || '',
      branch: schedule?.branch || '',
      description: schedule?.description || '',
      date: schedule?.date || '',
      time: timeString,
      weekDays: weekDaysBR,
      monthDay: schedule?.monthDay ? String(schedule.monthDay) : this.form.get('monthDay').value,
      frequencyMode: schedule?.frequencyMode || 'simple',
      intervalUnit: schedule?.intervalUnit || this.form.get('intervalUnit').value,
      intervalValue: schedule?.intervalValue || this.form.get('intervalValue').value,
    }, { emitEvent: false });

    const executionType = schedule?.executionType || 'once';
    this.form.get('executionType').setValue(executionType);
    this.form.get('testCases')?.setValue(this.selectedTestCases, { emitEvent: false });
    this.filterBranches(this.branchSearchControl.value);
    this.filterTestCases(this.testCaseSearchControl.value);

    if (executionType === 'recurring') {
      if (schedule?.frequencyMode === 'interval') {
        this.form.get('frequencyMode').setValue('interval');
      } else {
        const simpleFrequency = schedule?.simpleFrequency || this.form.get('simpleFrequency').value;
        this.form.get('frequencyMode').setValue('simple');
        this.form.get('simpleFrequency').setValue(simpleFrequency);
        if (simpleFrequency === 'weekly') {
          this.form.get('weekDays').setValue(weekDaysBR);
        }
        if (simpleFrequency === 'monthly' && schedule?.monthDay) {
          this.form.get('monthDay').setValue(String(schedule.monthDay));
        }
      }
    } else if (executionType === 'once' && schedule?.time) {
      this.form.get('time').setValue(timeString);
    }
  }

  private buildTimeString(time?: { hour: number; minute: number }): string {
    if (!time || typeof time.hour === 'undefined' || typeof time.minute === 'undefined') {
      return this.form.get('time').value;
    }

    const hour = String(time.hour).padStart(2, '0');
    const minute = String(time.minute).padStart(2, '0');
    return `${hour}:${minute}`;
  }

  private translateWeekDaysToPortuguese(weekDays?: string[]): string[] {
    if (!weekDays || weekDays.length === 0) {
      return [];
    }

    const diasSemanaIngles = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const diasSemanaPortugues = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    return weekDays.map((dia) => {
      const index = diasSemanaIngles.indexOf(dia);
      return index !== -1 ? diasSemanaPortugues[index] : dia;
    });
  }

  onBranchSelectOpened(opened: boolean): void {
    if (opened) {
      setTimeout(() => this.branchSearchInput?.nativeElement?.focus(), 100);
    }
  }

  onTestCasesSelectOpened(opened: boolean): void {
    if (opened) {
      setTimeout(() => this.testCasesSearchInput?.nativeElement?.focus(), 100);
    }
  }

  clearBranchSearch(event: MouseEvent): void {
    event.stopPropagation();
    this.branchSearchControl.setValue('');
  }

  clearTestCaseSearch(event: MouseEvent): void {
    event.stopPropagation();
    this.testCaseSearchControl.setValue('');
  }

  onToggleSelectAllTestCases(event: MatOptionSelectionChange): void {
    if (!event.isUserInput) {
      return;
    }
    event.source.deselect();

    const filteredNames = this.getFilteredTestCaseNames();
    if (!filteredNames.length) {
      return;
    }

    if (this.isAllFilteredTestCasesSelected()) {
      const remaining = this.selectedTestCases.filter((name) => !filteredNames.includes(name));
      this.form.get('testCases')?.setValue(remaining);
    } else {
      const union = Array.from(new Set([...this.selectedTestCases, ...filteredNames]));
      this.form.get('testCases')?.setValue(union);
    }
  }

  onToggleSelectAllTestCasesCheckbox(event: MatCheckboxChange): void {
    if (event?.checked) {
      const filteredNames = this.getFilteredTestCaseNames();
      const union = Array.from(new Set([...this.selectedTestCases, ...filteredNames]));
      this.form.get('testCases')?.setValue(union);
    } else {
      const filteredNames = this.getFilteredTestCaseNames();
      const remaining = this.selectedTestCases.filter((name) => !filteredNames.includes(name));
      this.form.get('testCases')?.setValue(remaining);
    }
  }

  isAllFilteredTestCasesSelected(): boolean {
    const filteredNames = this.getFilteredTestCaseNames();
    return filteredNames.length > 0 && filteredNames.every((name) => this.selectedTestCases.includes(name));
  }

  isSomeFilteredTestCasesSelected(): boolean {
    const filteredNames = this.getFilteredTestCaseNames();
    const selectedCount = filteredNames.filter((name) => this.selectedTestCases.includes(name)).length;
    return selectedCount > 0 && selectedCount < filteredNames.length;
  }

  private filterBranches(searchTerm?: string): void {
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) {
      this.filteredBranches = [...this.branches];
      return;
    }
    this.filteredBranches = this.branches.filter((branch) => branch.toLowerCase().includes(term));
  }

  private filterTestCases(searchTerm?: string): void {
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) {
      this.filteredTestCasesList = [...this.testCasesList];
      return;
    }
    this.filteredTestCasesList = this.testCasesList.filter((testCase) =>
      testCase?.name?.toLowerCase().includes(term)
    );
  }

  private getFilteredTestCaseNames(): string[] {
    return this.filteredTestCasesList.map((testCase) => testCase?.name).filter(Boolean);
  }

  private fetchTestCasesRecursively(
    projectId: string,
    page: number,
    accumulator: Array<{ name: string }>
  ): void {
    const limit = this.testCasesPageSize;
    this.services
      .genericGetNoProject<{ count: number; rows: Array<{ name: string }> }>(
        `test-cases?projectId=${projectId}&page=${page}&limit=${limit}`
      )
      .subscribe({
        next: (value) => {
          const rows = value?.rows || [];
          const count = value?.count ?? rows.length;
          const merged = [...accumulator, ...rows];
          this.testCasesList = merged;
          this.filterTestCases(this.testCaseSearchControl.value);

          if (merged.length < count && rows.length > 0) {
            this.fetchTestCasesRecursively(projectId, page + 1, merged);
          }
        },
        error: () => {
          this.testCasesList = accumulator;
          this.filterTestCases(this.testCaseSearchControl.value);
        },
      });
  }

  
}

